/**
 * ONE WAY TO WRITE A DATE ON SCREEN (Taiga #1538): `dd-MM-yyyy`, and `dd-MM-yyyy hh:mm`
 * with the half of the day in the reader's language when there is a time — the shape
 * `DateUtil.FormatDateTime` has always printed and most screens already showed.
 *
 * The API answers in five other shapes besides (`2026-08-17`, `2026/09/17 13:27:11`,
 * `2024-05-08 15:48:42`, an ISO instant…), and they reached the page as they came, so two
 * neighbouring lists disagreed on how to write a day. This only changes how a value LOOKS:
 * sorting and comparison keep reading the value the server sent.
 *
 * No imports, so a plain `node --test` can load it; `dates.js` binds the UI language.
 */

const ISO = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/
const DMY = /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*(AM|PM|ص|م))?)?$/i

const pad = (n) => String(n).padStart(2, '0')
const half = (pm, english) => (english ? (pm ? 'PM' : 'AM') : pm ? 'م' : 'ص')
const clock = (hours, minutes, english) =>
  `${pad(hours % 12 || 12)}:${pad(minutes)} ${half(hours >= 12, english)}`

/**
 * @param {unknown} value  what the API sent
 * @param {{ english?: boolean }} [options]
 * @returns {string} the value in the one display shape, or as it came when it is not a date
 */
export function formatDisplayDate(value, { english = false } = {}) {
  if (value == null || value === '') return ''
  const text = String(value).trim()

  const iso = text.match(ISO)
  if (iso) {
    let [, year, month, day, hours, minutes, zone] = iso
    /* An instant with a zone is shown on the reader's clock; a bare wall time is not moved. */
    if (zone && hours != null) {
      const at = new Date(text.replace(' ', 'T'))
      if (!Number.isNaN(at.getTime())) {
        ;[year, month, day, hours, minutes] = [at.getFullYear(), at.getMonth() + 1, at.getDate(), at.getHours(), at.getMinutes()]
      }
    }
    const date = `${pad(day)}-${pad(month)}-${year}`
    return hours == null ? date : `${date} ${clock(Number(hours), Number(minutes), english)}`
  }

  const dmy = text.match(DMY)
  if (dmy) {
    const [, day, month, year, hours, minutes, marker] = dmy
    const date = `${pad(day)}-${pad(month)}-${year}`
    if (hours == null) return date
    if (!marker) return `${date} ${clock(Number(hours), Number(minutes), english)}`
    return `${date} ${pad(hours)}:${minutes} ${half(/PM|م/i.test(marker), english)}`
  }

  return text
}
